import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Award, Plus, Edit3, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCaseStudiesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/case-studies`);
      setItems(data || []);
    } catch {
      toast.error("Failed to load case studies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete case study "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/case-studies/${slug}`);
      setItems((prev) => prev.filter((c) => c.slug !== slug));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const handleTogglePublish = async (cs) => {
    try {
      const { data } = await axios.patch(`${API}/admin/case-studies/${cs.slug}`, {
        published: !cs.published,
      });
      setItems((prev) => prev.map((c) => (c.slug === cs.slug ? { ...c, ...data } : c)));
      toast.success(data.published ? "Published." : "Unpublished.");
    } catch {
      toast.error("Update failed.");
    }
  };

  return (
    <div data-testid="admin-case-studies-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <Award className="h-4 w-4 text-sky-600" />
            <h1 className="text-base font-bold">Case Studies</h1>
            <span className="text-xs text-slate-400">({items.length})</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Customer success stories shown on /case-studies.</p>
        </div>
        <Button
          onClick={() => navigate("/admin/case-studies/new")}
          data-testid="admin-cs-new"
          className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> New case study
        </Button>
      </div>

      <div className="p-5 lg:p-8">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-sm text-slate-500">No case studies yet - create your first one.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((cs) => (
              <div
                key={cs.slug}
                data-testid={`admin-cs-card-${cs.slug}`}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-sky-300 hover:shadow-md transition-all"
              >
                <div className="aspect-[16/10] bg-slate-100 relative">
                  {cs.image ? (
                    <img src={cs.image} alt={cs.company} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <Award className="h-10 w-10" />
                    </div>
                  )}
                  {!cs.published && (
                    <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                      Draft
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-bold text-sky-600 uppercase tracking-widest mb-1">{cs.industry}</p>
                  <h3 className="text-sm font-bold text-slate-900 line-clamp-2 leading-snug">{cs.headline}</h3>
                  <p className="text-[10px] text-slate-400 mt-1 font-mono">/case-studies/{cs.slug}</p>
                  <p className="text-xs text-slate-500 mt-2 line-clamp-2">{cs.excerpt}</p>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-end gap-1">
                    <Link
                      to={`/case-studies/${cs.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View on site"
                      className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleTogglePublish(cs)}
                      data-testid={`admin-cs-toggle-${cs.slug}`}
                      title={cs.published ? "Unpublish" : "Publish"}
                      className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      {cs.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                    <Link
                      to={`/admin/case-studies/${cs.slug}`}
                      data-testid={`admin-cs-edit-${cs.slug}`}
                      className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(cs.slug)}
                      data-testid={`admin-cs-delete-${cs.slug}`}
                      title="Delete"
                      className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
